// Admin "Users" — admin console screen 4 of 5, route /users (deliberately
// not "/admin/users" — see App.jsx's own route-table comment and
// components/AdminNav.jsx's header comment: any path starting with "/admin"
// collides with the dev proxy's/Nginx's API path prefix for a real browser
// navigation, not just fetch()).
//
// Originally scoped to create/deactivate SALES accounts only. BROADENED by
// the customers/products/users multi-select bulk-actions task: this screen
// now also lists admin accounts (hooks/useAdminUsers.js#useAdminStaffUsers,
// a deliberately separate query from the assignee-picker's sales-only
// read) and offers reactivate + bulk deactivate/reactivate/delete — the
// self/last-active-admin protections that task made non-negotiable are
// meaningless if an admin is never visible or selectable here. Account
// creation is still not self-service for ADMIN (this task never asked for
// that) — but, as of migration 025's third staff role, the create form
// (UserForm.jsx) now has a role picker and can create either a 'sales' or
// a 'catalog_manager' account (POST /admin/users, routes/
// adminUserRoutes.js's own createUserSchema).
//
// GET /admin/users?includeAdmins=1 returns every staff account, active and
// inactive both — an inactive row stays listed here, visually dimmed, not
// hidden, same as before.

import { useState, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useAdminStaffUsers,
  useCreateSalesUser,
  useDeactivateStaffUser,
  useReactivateStaffUser,
  useBulkDeactivateStaff,
  useBulkReactivateStaff,
  useBulkDeleteStaff,
  fetchStaffBulkDeletePreview,
  useSetUserPermission,
  usePermissionCatalog,
} from '../hooks/useAdminUsers.js';
import { useRowSelection } from '../hooks/useRowSelection.js';
import { confirmAndBulkAction, confirmAndBulkDelete } from '../utils/bulkActions.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import BulkActionBar from '../components/BulkActionBar.jsx';
import UserForm from '../components/UserForm.jsx';
import { formatDateTime } from '../utils/formatDate.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import { useActionFeedback } from '../hooks/useActionFeedback.js';
import styles from './UsersPage.module.css';

// Renders whatever GET /admin/users/permissions returns (utils/
// permissionCatalog.js) — no hardcoded local list. Adding a permission is a
// backend-only change (that catalogue file) plus its two i18n strings; this
// component needs no edit.
function PermissionToggles({ user, permissionCatalog }) {
  const { t } = useLanguage();
  const setPermissionMutation = useSetUserPermission();
  const { toastOk, toastErr } = useActionFeedback();

  // Which permissions on THIS row currently have a request in flight. A
  // Set, not a single value — sibling permissions on the same user can be
  // toggled concurrently (useSetUserPermission is one shared mutation
  // instance per row), and `mutation.variables` only ever reflects the
  // most recent mutate() call, so it can't tell two overlapping toggles
  // apart. Each permission is added right before its own mutate() call and
  // removed in that call's own onSettled, so it tracks exactly the calls
  // this row has actually issued.
  //
  // Declared before the is_active/role early return below (not after it)
  // so every hook in this component runs on every render regardless of
  // which branch that check takes — a sales user's own row can flip
  // is_active via this same component instance re-rendering (not
  // remounting) after a deactivate, which would otherwise change the
  // number of hooks called between renders.
  const [pendingPermissions, setPendingPermissions] = useState(() => new Set());

  const toggle = useCallback(
    (permission, granted) => {
      if (pendingPermissions.has(permission)) return; // guards a double-click
      setPendingPermissions((prev) => new Set(prev).add(permission));
      setPermissionMutation.mutate(
        { id: user.id, permission, granted },
        {
          onSuccess: () =>
            toastOk(t(granted ? 'users.permission.grantedToast' : 'users.permission.revokedToast')),
          onError: (err) => toastErr(err, ['USER_NOT_FOUND', 'UNKNOWN_PERMISSION']),
          onSettled: () =>
            setPendingPermissions((prev) => {
              const next = new Set(prev);
              next.delete(permission);
              return next;
            }),
        }
      );
    },
    [pendingPermissions, setPermissionMutation, user.id, t, toastOk, toastErr]
  );

  // Permissions are a sales-account concept — an admin already can do
  // everything, and an inactive account can hold no live grant.
  if (user.role !== 'sales' || !user.is_active) {
    return <span className={styles.permissionNa}>—</span>;
  }

  const held = new Set(user.permissions || []);

  return (
    <div className={styles.permissionList}>
      {(permissionCatalog || []).map((permission) => {
        const checked = held.has(permission);
        const isPending = pendingPermissions.has(permission);
        return (
          <div key={permission}>
            <label className={`${styles.permissionToggle} ${isPending ? styles.disabled : ''}`}>
              <input
                type="checkbox"
                checked={checked}
                disabled={isPending}
                onChange={(e) => toggle(permission, e.target.checked)}
              />
              <span>{t(`users.permission.${permission}.label`)}</span>
            </label>
            <span className={styles.permissionHint}>
              {t(`users.permission.${permission}.hint`)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function UserRow({ user, permissionCatalog, selection }) {
  const { t, lang } = useLanguage();
  const deactivateMutation = useDeactivateStaffUser();
  const reactivateMutation = useReactivateStaffUser();
  const { confirm, toastOk, toastErr } = useActionFeedback();

  const handleDeactivate = async () => {
    const ok = await confirm({
      title: t('users.actions.deactivateConfirmTitle', { name: user.full_name }),
      message: t('users.actions.deactivateConfirmBody'),
      confirmLabel: t('users.actions.deactivateConfirm'),
      tone: 'danger',
    });
    if (!ok) return;
    deactivateMutation.mutate(user.id, {
      onSuccess: () => toastOk(t('users.deactivatedToast')),
      onError: (err) =>
        toastErr(err, ['USER_NOT_FOUND', 'CANNOT_ACT_ON_SELF', 'LAST_ACTIVE_ADMIN']),
    });
  };

  const handleReactivate = () => {
    reactivateMutation.mutate(user.id, {
      onSuccess: () => toastOk(t('users.reactivatedToast')),
      onError: (err) => toastErr(err, ['USER_NOT_FOUND', 'USER_ALREADY_ACTIVE']),
    });
  };

  return (
    <tr className={`${styles.row} ${!user.is_active ? styles.rowInactive : ''}`}>
      <td className={styles.checkboxCell}>
        <input
          type="checkbox"
          checked={selection.isSelected(user.id)}
          onChange={() => selection.toggle(user.id)}
          aria-label={user.full_name}
        />
      </td>
      <td>{user.full_name}</td>
      <td>{user.email}</td>
      <td>{user.phone || t('users.phoneNotSet')}</td>
      {/* Third staff role (migration 025): the role word itself already
          differs ("Sales" vs "Catalog manager" vs "Admin"), but
          catalog_manager gets the stamp-violet weight AdminNav/refLink-style
          links already use elsewhere in this app — a text-weight/colour
          distinction, not a new chip/pill (root CLAUDE.md reserves coloured
          chips/pills for the signature status rule only, per this file's
          own CSS header comment). */}
      <td>
        <span className={user.role === 'catalog_manager' ? styles.roleCatalogManager : undefined}>
          {t(`users.role.${user.role}`)}
        </span>
      </td>
      <td>
        <span className={user.is_active ? styles.statusActive : styles.statusInactive}>
          {t(user.is_active ? 'users.status.active' : 'users.status.inactive')}
        </span>
      </td>
      <td className="tabularNums">
        {formatDateTime(user.last_login_at, lang) || t('users.neverLoggedIn')}
      </td>
      <td className={styles.permissionCell}>
        <PermissionToggles user={user} permissionCatalog={permissionCatalog} />
      </td>
      <td className={styles.actionsCell}>
        {user.is_active ? (
          <button
            type="button"
            className={styles.deactivateLink}
            onClick={handleDeactivate}
            disabled={deactivateMutation.isPending}
          >
            {deactivateMutation.isPending && <Spinner />}{' '}
            {deactivateMutation.isPending
              ? t('users.actions.deactivating')
              : t('users.actions.deactivate')}
          </button>
        ) : (
          <button
            type="button"
            className={styles.linkButton}
            onClick={handleReactivate}
            disabled={reactivateMutation.isPending}
          >
            {reactivateMutation.isPending && <Spinner />}{' '}
            {reactivateMutation.isPending
              ? t('users.actions.reactivating')
              : t('users.actions.reactivate')}
          </button>
        )}
      </td>
    </tr>
  );
}

export default function UsersPage() {
  const { t } = useLanguage();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const { confirm, toastOk, toastErr } = useActionFeedback();

  const { data: users, isLoading, isError, error, refetch } = useAdminStaffUsers();
  const { data: permissionCatalog } = usePermissionCatalog();
  const createMutation = useCreateSalesUser();

  const allUsers = users || [];
  const isTrulyEmpty = !isLoading && !isError && allUsers.length === 0;

  const selection = useRowSelection(allUsers);
  const bulkDeactivate = useBulkDeactivateStaff();
  const bulkReactivate = useBulkReactivateStaff();
  const bulkDelete = useBulkDeleteStaff();

  const handleCreate = (body) => {
    createMutation.mutate(body, {
      onSuccess: () => {
        setShowCreateForm(false);
        toastOk(t('users.createdToast'));
      },
      onError: (err) => toastErr(err, ['DUPLICATE_EMAIL', 'VALIDATION_ERROR']),
    });
  };

  const handleBulkDeactivate = () =>
    confirmAndBulkAction({
      ids: selection.selectedIds,
      confirm,
      t,
      titleId: 'bulk.deactivateConfirmTitle',
      messageId: 'bulk.deactivateConfirmBody',
      confirmLabelId: 'bulk.deactivateConfirmButton',
      actionFn: (ids) => bulkDeactivate.mutateAsync(ids),
      toastOk,
      toastErr,
      onSettled: () => selection.clear(),
    });

  const handleBulkReactivate = () =>
    confirmAndBulkAction({
      ids: selection.selectedIds,
      confirm,
      t,
      titleId: 'bulk.reactivateConfirmTitle',
      messageId: 'bulk.reactivateConfirmBody',
      confirmLabelId: 'bulk.reactivateConfirmButton',
      actionFn: (ids) => bulkReactivate.mutateAsync(ids),
      toastOk,
      toastErr,
      onSettled: () => selection.clear(),
    });

  const handleBulkDelete = () =>
    confirmAndBulkDelete({
      ids: selection.selectedIds,
      confirm,
      t,
      previewFn: fetchStaffBulkDeletePreview,
      deleteFn: (ids, confirmedCount) => bulkDelete.mutateAsync({ ids, confirmedCount }),
      toastOk,
      toastErr,
      onSettled: () => selection.clear(),
    });

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('users.title')}</h1>
      <p className={styles.subtitle}>{t('users.subtitle')}</p>

      <div className={styles.filterRow}>
        <button
          type="button"
          className={styles.newUserButton}
          onClick={() => setShowCreateForm((prev) => !prev)}
        >
          {t('users.newUser')}
        </button>
      </div>

      {showCreateForm && (
        <UserForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateForm(false)}
          isPending={createMutation.isPending}
        />
      )}

      <BulkActionBar
        count={selection.count}
        onClear={selection.clear}
        actions={[
          {
            id: 'deactivate',
            labelId: 'bulk.deactivate',
            onClick: handleBulkDeactivate,
            pending: bulkDeactivate.isPending,
          },
          {
            id: 'reactivate',
            labelId: 'bulk.reactivate',
            onClick: handleBulkReactivate,
            pending: bulkReactivate.isPending,
          },
          {
            id: 'delete',
            labelId: 'bulk.delete',
            tone: 'danger',
            onClick: handleBulkDelete,
            pending: bulkDelete.isPending,
          },
        ]}
      />

      {isLoading && <LoadingState variant="rows" rows={6} />}

      {isError && !isLoading && (
        <ErrorState messageId="users.errorBody" error={error} onRetry={refetch} />
      )}

      {isTrulyEmpty && <EmptyState titleId="users.emptyTitle" bodyId="users.emptyBody" />}

      {!isLoading && !isError && allUsers.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <th className={styles.checkboxCell}>
                <input
                  type="checkbox"
                  aria-label={t('bulk.selectAll')}
                  checked={selection.allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = selection.someSelected;
                  }}
                  onChange={selection.toggleAll}
                />
              </th>
              <th>{t('users.col.name')}</th>
              <th>{t('users.col.email')}</th>
              <th>{t('users.col.phone')}</th>
              <th>{t('users.col.role')}</th>
              <th>{t('users.col.status')}</th>
              <th>{t('users.col.lastLogin')}</th>
              <th>{t('users.col.permissions')}</th>
              <th>{t('users.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {allUsers.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                permissionCatalog={permissionCatalog}
                selection={selection}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
